import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './index.css';
import noticeIcon from '@/assets/imgs/news/notice.svg';
import breadcrumbArrow from '@/assets/imgs/news/breadcrumb-arrow.svg';
import backArrow from '@/assets/imgs/news/back-arrow.svg';
import fallbackNews1 from '@/assets/imgs/home/news/news-1.png';
import fallbackNews2 from '@/assets/imgs/home/news/news-2.png';
import fallbackNews3 from '@/assets/imgs/home/news/news-3.png';
import { newsDetail, newsPage, type NewsRecord } from '@/service/api/news';
import { NewsHero } from '../components/NewsHero';

const RELATED_NEWS_COUNT = 3;
const fallbackImages = [fallbackNews1, fallbackNews2, fallbackNews3];

function formatNewsDate(value?: string, separator = '/') {
  const match = value?.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (!match) return ['----', '--', '--'].join(separator);

  return [match[1], match[2].padStart(2, '0'), match[3].padStart(2, '0')].join(
    separator,
  );
}

function sanitizeNewsHtml(html: string) {
  if (!html || typeof window === 'undefined') return html;

  const documentNode = new DOMParser().parseFromString(html, 'text/html');
  documentNode.querySelectorAll('script, iframe, object, embed').forEach(node => node.remove());

  documentNode.querySelectorAll('*').forEach(node => {
    Array.from(node.attributes).forEach(attribute => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      if (name.startsWith('on') || ((name === 'href' || name === 'src') && value.startsWith('javascript:'))) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  return documentNode.body.innerHTML;
}

function NewsTag() {
  const { t } = useTranslation();

  return (
    <span className="news-detail-tag">
      <img src={noticeIcon} alt="" />
      {t('news.list.notice')}
    </span>
  );
}

type RelatedNewsCardProps = {
  fromPage: number;
  index: number;
  record: NewsRecord;
};

function RelatedNewsCard({ fromPage, index, record }: RelatedNewsCardProps) {
  const content = record.title?.trim() || record.summary?.trim() || '';
  const detailUrl = `/news-detail?id=${record.id ?? ''}&fromPage=${fromPage}`;

  return (
    <article className="news-detail-related-card">
      <Link
        to={detailUrl}
        className="news-detail-related-card__image-link"
        aria-label={content}
      >
        <img
          src={record.coverImg || fallbackImages[index % fallbackImages.length]}
          alt=""
          className="news-detail-related-card__image"
        />
      </Link>
      <Link to={detailUrl} className="news-detail-related-card__title">
        {content}
      </Link>
      <div className="news-detail-related-card__meta">
        <NewsTag />
        <time dateTime={record.publishTime}>{formatNewsDate(record.publishTime, '.')}</time>
      </div>
    </article>
  );
}

function DetailSkeleton() {
  return (
    <div className="news-detail-layout news-detail-skeleton" aria-hidden="true">
      <div className="news-detail-skeleton__main">
        <div className="news-detail-skeleton__image" />
        <div className="news-detail-skeleton__title" />
        <div className="news-detail-skeleton__line" />
        <div className="news-detail-skeleton__line news-detail-skeleton__line--short" />
      </div>
      <div className="news-detail-skeleton__aside" />
    </div>
  );
}

const NewsDetail = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const newsId = searchParams.get('id')?.trim() ?? '';
  const fromPageParam = Number(searchParams.get('fromPage'));
  const fromPage = Number.isInteger(fromPageParam) && fromPageParam > 0 ? fromPageParam : 1;
  const newsListUrl = fromPage > 1 ? `/news?page=${fromPage}` : '/news';
  const [record, setRecord] = useState<NewsRecord | null>(null);
  const [relatedNews, setRelatedNews] = useState<NewsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let ignore = false;
    const numericNewsId = Number(newsId);

    if (!newsId || !Number.isFinite(numericNewsId) || numericNewsId <= 0) {
      setRecord(null);
      setRelatedNews([]);
      setLoading(false);
      setError(true);
      return () => {
        ignore = true;
      };
    }

    setLoading(true);
    setError(false);

    Promise.allSettled([
      newsDetail(newsId),
      newsPage({ current: 1, size: RELATED_NEWS_COUNT + 1, status: 1 }),
    ])
      .then(([detailRequest, pageRequest]) => {
        if (ignore) return;
        if (detailRequest.status === 'rejected') throw detailRequest.reason;

        const detailResult = detailRequest.value;
        if (detailResult.code !== 0 || !detailResult.data) {
          throw new Error(detailResult.msg || 'Unable to load news detail');
        }

        setRecord(detailResult.data);
        const pageResult = pageRequest.status === 'fulfilled' ? pageRequest.value : null;
        setRelatedNews(
          (pageResult?.code === 0 ? pageResult.data?.records ?? [] : [])
            .filter(item => String(item.id) !== newsId)
            .slice(0, RELATED_NEWS_COUNT),
        );
      })
      .catch(() => {
        if (ignore) return;
        setRecord(null);
        setRelatedNews([]);
        setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [newsId, i18n.resolvedLanguage]);

  const safeContent = useMemo(
    () => sanitizeNewsHtml(record?.content?.trim() ?? ''),
    [record?.content],
  );

  return (
    <div className="news-detail-page">
      <NewsHero />

      <nav className="news-detail-breadcrumb" aria-label={t('news.detail.breadcrumb')}>
        <Link to="/">{t('common.navigation.home')}</Link>
        <img src={breadcrumbArrow} alt="" />
        <Link to={newsListUrl}>{t('news.list.title')}</Link>
        <img src={breadcrumbArrow} alt="" />
        <span>{t('news.detail.pageTitle')}</span>
      </nav>

      <section className="news-detail-content">
        {loading ? (
          <DetailSkeleton />
        ) : error || !record ? (
          <div className="news-detail-empty">{t('news.detail.error')}</div>
        ) : (
          <>
            <div className="news-detail-meta">
              <NewsTag />
              <time dateTime={record.publishTime}>{formatNewsDate(record.publishTime)}</time>
            </div>

            <div className="news-detail-layout">
              <article className="news-detail-article">
                <img
                  src={record.coverImg || fallbackImages[0]}
                  alt=""
                  className="news-detail-article__cover"
                />
                <h2>{record.title}</h2>
                {safeContent && (
                  <div
                    className="news-detail-article__body"
                    dangerouslySetInnerHTML={{ __html: safeContent }}
                  />
                )}
              </article>

              <aside className="news-detail-related" aria-labelledby="related-news-title">
                <h2 id="related-news-title">
                  <span aria-hidden="true" />
                  {t('news.detail.related')}
                </h2>
                {relatedNews.length > 0 ? (
                  <div className="news-detail-related__list">
                    {relatedNews.map((item, index) => (
                      <RelatedNewsCard
                        key={item.id ?? `${item.createTime}-${index}`}
                        record={item}
                        index={index}
                        fromPage={fromPage}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="news-detail-related__empty">{t('news.list.empty')}</p>
                )}
              </aside>
            </div>
          </>
        )}

        <Link to={newsListUrl} className="news-detail-back">
          <span className="news-detail-back__content">
            {t('news.detail.back')}
            <span className="news-detail-back__arrow" aria-hidden="true">
              <img src={backArrow} alt="" />
            </span>
          </span>
        </Link>
      </section>
    </div>
  );
};

export default NewsDetail;
